import React, { useState } from 'react';
import { 
  Activity, 
  Search, 
  Filter,
  User,
  Calendar,
  Clock,
  FileText,
  Settings,
  Package,
  Truck,
  Shield
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

// Mock data for activity logs
const mockActivityLogs = [
  {
    id: 'log_001',
    timestamp: '2025-01-13T14:30:00+07:00',
    user: 'admin@system.com',
    userName: 'Admin System',
    action: 'create_order',
    actionName: 'Tạo đơn hàng',
    target: 'DH001',
    targetType: 'order',
    details: 'Tạo đơn hàng mới từ Nguyễn Văn A đến Trần Văn B',
    ipAddress: '192.168.1.100',
    userAgent: 'Mozilla/5.0...',
    status: 'success',
    category: 'order_management'
  },
  {
    id: 'log_002',
    timestamp: '2025-01-13T13:15:00+07:00',
    user: 'staff001@company.com',
    userName: 'Nhân viên Lê C',
    action: 'update_order_status',
    actionName: 'Cập nhật trạng thái đơn hàng',
    target: 'DH002',
    targetType: 'order',
    details: 'Chuyển trạng thái từ "Chờ xử lý" thành "Đang giao"',
    ipAddress: '192.168.1.101',
    userAgent: 'Mozilla/5.0...',
    status: 'success',
    category: 'order_management'
  },
  {
    id: 'log_003',
    timestamp: '2025-01-13T12:45:00+07:00',
    user: 'manager001@company.com',
    userName: 'Quản lý Phạm D',
    action: 'create_hub',
    actionName: 'Tạo bưu cục',
    target: 'HUB003',
    targetType: 'hub',
    details: 'Tạo bưu cục mới tại Đà Nẵng',
    ipAddress: '192.168.1.102',
    userAgent: 'Mozilla/5.0...',
    status: 'success',
    category: 'hub_management'
  },
  {
    id: 'log_004',
    timestamp: '2025-01-13T11:30:00+07:00',
    user: 'staff002@company.com',
    userName: 'Nhân viên Nguyễn E',
    action: 'login_failed',
    actionName: 'Đăng nhập thất bại',
    target: 'staff002@company.com',
    targetType: 'user',
    details: 'Đăng nhập thất bại - sai mật khẩu',
    ipAddress: '192.168.1.103',
    userAgent: 'Mozilla/5.0...',
    status: 'failed',
    category: 'authentication'
  },
  {
    id: 'log_005',
    timestamp: '2025-01-13T10:15:00+07:00',
    user: 'system',
    userName: 'Hệ thống',
    action: 'auto_reconciliation',
    actionName: 'Đối soát tự động',
    target: 'REC_2025_01_13',
    targetType: 'reconciliation',
    details: 'Thực hiện đối soát COD tự động cho tuần 02/2025',
    ipAddress: 'system',
    userAgent: 'system',
    status: 'success',
    category: 'reconciliation'
  }
];

const ActivityLog = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedLog, setSelectedLog] = useState(null);

  const filteredLogs = mockActivityLogs.filter(log => {
    const matchesSearch = log.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.actionName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.target.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         log.details.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || log.category === filterCategory;
    const matchesStatus = filterStatus === 'all' || log.status === filterStatus;
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const getActionIcon = (action) => {
    switch (action) {
      case 'create_order':
      case 'update_order_status':
        return <Package className="h-4 w-4 text-blue-600" />;
      case 'create_hub':
      case 'update_hub':
        return <Truck className="h-4 w-4 text-green-600" />;
      case 'login_failed':
      case 'login_success':
        return <Shield className="h-4 w-4 text-purple-600" />;
      case 'auto_reconciliation':
        return <FileText className="h-4 w-4 text-orange-600" />;
      default:
        return <Settings className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'order_management':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'hub_management':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'authentication':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
      case 'reconciliation':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const getCategoryName = (category) => {
    switch (category) {
      case 'order_management': return 'Quản lý đơn hàng';
      case 'hub_management': return 'Quản lý bưu cục';
      case 'authentication': return 'Xác thực';
      case 'reconciliation': return 'Đối soát';
      default: return 'Khác';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-foreground">Lịch sử hoạt động</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Danh sách hoạt động */}
        <Card className="lg:col-span-2 animate-fade-in">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <Activity className="h-5 w-5 mr-2 text-primary" />
                Nhật ký hoạt động
              </CardTitle>
              <div className="flex space-x-2">
                <div className="relative">
                  <Search className="h-4 w-4 absolute left-3 top-3 text-muted-foreground" />
                  <Input
                    placeholder="Tìm kiếm..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 w-48"
                  />
                </div>
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md text-sm bg-background"
                >
                  <option value="all">Tất cả danh mục</option>
                  <option value="order_management">Quản lý đơn hàng</option>
                  <option value="hub_management">Quản lý bưu cục</option>
                  <option value="authentication">Xác thực</option>
                  <option value="reconciliation">Đối soát</option>
                </select>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="px-3 py-2 border border-input rounded-md text-sm bg-background"
                >
                  <option value="all">Tất cả trạng thái</option>
                  <option value="success">Thành công</option>
                  <option value="failed">Thất bại</option>
                  <option value="warning">Cảnh báo</option>
                </select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredLogs.map((log) => (
                <div
                  key={log.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all duration-200 hover:shadow-md ${
                    selectedLog?.id === log.id ? 'border-primary bg-primary/5' : 'border-border'
                  }`}
                  onClick={() => setSelectedLog(log)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="bg-primary/10 p-2 rounded-lg">
                        {getActionIcon(log.action)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{log.actionName}</h3>
                          <Badge className={getCategoryColor(log.category)}>
                            {getCategoryName(log.category)}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {new Date(log.timestamp).toLocaleString('vi-VN')}
                        </p>
                      </div>
                    </div>
                    <Badge className={getStatusColor(log.status)}>
                      {log.status === 'success' ? 'Thành công' : log.status === 'failed' ? 'Thất bại' : 'Cảnh báo'}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{log.userName}</span>
                    </div>
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 mr-2 text-muted-foreground" />
                      <span>{log.target}</span>
                    </div>
                    <p className="text-muted-foreground">{log.details}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Chi tiết hoạt động */}
        <Card className="animate-fade-in">
          <CardHeader>
            <CardTitle>Chi tiết hoạt động</CardTitle>
          </CardHeader>
          <CardContent>
            {selectedLog ? (
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold mb-2">Thông tin cơ bản</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Hành động:</span> {selectedLog.actionName}</p>
                    <p><span className="font-medium">Thời gian:</span> {new Date(selectedLog.timestamp).toLocaleString('vi-VN')}</p>
                    <p><span className="font-medium">Trạng thái:</span> 
                      <Badge className={`ml-2 ${getStatusColor(selectedLog.status)}`}>
                        {selectedLog.status === 'success' ? 'Thành công' : selectedLog.status === 'failed' ? 'Thất bại' : 'Cảnh báo'}
                      </Badge>
                    </p>
                    <p><span className="font-medium">Danh mục:</span> 
                      <Badge className={`ml-2 ${getCategoryColor(selectedLog.category)}`}>
                        {getCategoryName(selectedLog.category)}
                      </Badge>
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold mb-2">Thông tin người dùng</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Tên:</span> {selectedLog.userName}</p>
                    <p><span className="font-medium">Email:</span> {selectedLog.user}</p>
                    <p><span className="font-medium">IP:</span> {selectedLog.ipAddress}</p>
                  </div>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold mb-2">Chi tiết hoạt động</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">Đối tượng:</span> {selectedLog.target}</p>
                    <p><span className="font-medium">Loại:</span> {selectedLog.targetType}</p>
                    <p><span className="font-medium">Mô tả:</span> {selectedLog.details}</p>
                  </div>
                </div>

                <div className="p-4 bg-muted/50 rounded-lg">
                  <h3 className="font-semibold mb-2">Thông tin kỹ thuật</h3>
                  <div className="space-y-2 text-sm">
                    <p><span className="font-medium">User Agent:</span></p>
                    <p className="text-xs text-muted-foreground break-all">{selectedLog.userAgent}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                <Activity className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <p>Chọn một hoạt động để xem chi tiết</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ActivityLog;